import { portablePictures } from './imageTransfer'
import { normalizeProject, slugify } from './model'
import { downloadBlob } from './download'
import type { StoryProject } from './types'
import { audioAssets, importAudioAssets } from './audioTransfer'

export const BRAMBLE_PROJECT_FORMAT = 'bramble-project'
export const BRAMBLE_PROJECT_FORMAT_VERSION = 1
// Pretty-printed exports can be larger than the compact request body accepted by
// the server, so the file chooser leaves enough room to re-import every normal save.
export const MAX_PROJECT_FILE_BYTES = 160_000_000

export type BrambleProjectFile = {
  format: typeof BRAMBLE_PROJECT_FORMAT
  formatVersion: typeof BRAMBLE_PROJECT_FORMAT_VERSION
  exportedAt: string
  project: StoryProject
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function createProjectFile(
  project: StoryProject,
  exportedAt = new Date().toISOString(),
): BrambleProjectFile {
  return {
    format: BRAMBLE_PROJECT_FORMAT,
    formatVersion: BRAMBLE_PROJECT_FORMAT_VERSION,
    exportedAt,
    project,
  }
}

export function serializeProjectFile(
  project: StoryProject,
  exportedAt?: string,
) {
  return `${JSON.stringify(createProjectFile(project, exportedAt), null, 2)}\n`
}

export function projectFileName(project: Pick<StoryProject, 'title'>) {
  return `${slugify(project.title, 'untitled-story')}.bramble.json`
}

export function parseProjectFile(contents: string): StoryProject {
  let decoded: unknown
  try {
    decoded = JSON.parse(contents)
  } catch {
    throw new Error('That file is not valid JSON.')
  }

  const file = objectValue(decoded)
  if (!file || file.format !== BRAMBLE_PROJECT_FORMAT) {
    throw new Error('That is not a BRAMBLE project export.')
  }
  if (file.formatVersion !== BRAMBLE_PROJECT_FORMAT_VERSION) {
    throw new Error(
      `This BRAMBLE project format is not supported. Expected version ${BRAMBLE_PROJECT_FORMAT_VERSION}.`,
    )
  }

  const project = objectValue(file.project)
  if (!project) throw new Error('The BRAMBLE project data is missing.')
  if (!Array.isArray(project.nodes) || project.nodes.length === 0) {
    throw new Error('A story needs at least one scene.')
  }
  if (project.nodes.length > 5_000) {
    throw new Error('This project has too many scenes.')
  }

  try {
    return normalizeProject(project)
  } catch (caught) {
    throw new Error(
      caught instanceof Error
        ? caught.message
        : 'The BRAMBLE project data is invalid.',
    )
  }
}

export function downloadProjectFile(project: StoryProject) {
  const contents = serializeProjectFile(project)
  const blob = new Blob([contents], { type: 'application/json;charset=utf-8' })
  downloadBlob(blob, projectFileName(project))
}

export async function downloadFullProjectFile(project: StoryProject, userId: string) {
  project = await portablePictures(project, userId)
  const assets = await audioAssets(project, userId)
  const contents = JSON.stringify({ ...createProjectFile(project), ...(Object.keys(assets).length ? { audioAssets: assets } : {}) }, null, 2) + '\n'
  const blob = new Blob([contents], { type: 'application/json;charset=utf-8' })
  if (blob.size > MAX_PROJECT_FILE_BYTES) throw new Error('This backup is too large. Reduce audio to 100 MB per story and try again.')
  downloadBlob(blob, projectFileName(project))
}

export async function parseFullProjectFile(contents: string, userId: string) {
  const project = parseProjectFile(contents)
  await importAudioAssets(project, JSON.parse(contents).audioAssets, userId)
  return project
}
